/**
 * Retry fetch on transient server failures (5xx) or network errors.
 *
 * Scope: GETs on the hot path that are idempotent and where a retry is
 * user-visible-latency-neutral (the visitor would already be waiting).
 * Today: /api/status/:qrCode on FeedbackPage mount, where Cloud Run
 * cold-starts can return 503 for ~1-2 seconds after scale-to-zero.
 *
 * Do NOT use for mutations (POST feedback, POST status report) — those
 * already have an offline queue for resilience, and retrying on ambiguous
 * failures would risk double-submits.
 */

const RETRIABLE_STATUS = new Set([502, 503, 504]);

interface RetryOptions {
  /** Max attempts, including the first. Default: 3 (1 initial + 2 retries). */
  maxAttempts?: number;
  /** Initial backoff in ms. Default: 800. Jittered +/- 20%. */
  initialBackoffMs?: number;
  /** Total time budget cap in ms across all retries. Default: 5000. */
  maxTotalMs?: number;
}

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  retry?: RetryOptions,
): Promise<Response> {
  const maxAttempts = retry?.maxAttempts ?? 3;
  const initialBackoffMs = retry?.initialBackoffMs ?? 800;
  const maxTotalMs = retry?.maxTotalMs ?? 5000;

  const startedAt = Date.now();
  let attempt = 0;
  let lastError: unknown;

  while (attempt < maxAttempts) {
    attempt++;
    try {
      const response = await fetch(input, init);
      if (response.ok || !RETRIABLE_STATUS.has(response.status)) {
        return response;
      }
      lastError = new Error(`HTTP ${response.status}`);
    } catch (err) {
      lastError = err;
    }

    const elapsed = Date.now() - startedAt;
    if (attempt >= maxAttempts || elapsed >= maxTotalMs) break;

    const base = initialBackoffMs * Math.pow(2, attempt - 1);
    const jitter = base * (0.8 + Math.random() * 0.4);
    const remaining = maxTotalMs - elapsed;
    const wait = Math.max(0, Math.min(jitter, remaining));

    if (import.meta.env.DEV) {
      console.warn(`[fetchWithRetry] attempt ${attempt} failed, retrying in ${Math.round(wait)}ms`, lastError);
    }
    await new Promise((r) => setTimeout(r, wait));
  }

  if (lastError instanceof Error) throw lastError;
  throw new Error('fetchWithRetry exhausted');
}
